import React, { useEffect, useState } from "react";
import { authClient } from "./defaults";
import styles from "../common.module.css";

const Profile = () => {
    const [profile, setProfile] = useState({
        name: { first_name: "", last_name: "", middle_name: "" },
        role: "",
        org: "",
        email: "",
    });

    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [originalProfile, setOriginalProfile] = useState({});
    const [popupMessage, setPopupMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await authClient.get("/profile", { withCredentials: true });

                if (response.status === 200) {
                    const userProfile = {
                        name: response.data.user.name || { first_name: "", last_name: "", middle_name: "" },
                        role: response.data.user.role || "Unknown",
                        org: response.data.user.org || "Unknown",
                        email: response.data.user.email || "Unknown",
                    };
                    setProfile(userProfile);
                    setOriginalProfile(userProfile);
                } else {
                    throw new Error("Failed to load profile.");
                }
            } catch (err) {
                console.error("Profile fetch error:", err);
                setPopupMessage("Could not fetch profile data.");
                setIsSuccess(false);
                setShowPopup(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (["first_name", "last_name", "middle_name"].includes(name)) {
            setProfile((prev) => ({
                ...prev,
                name: { ...prev.name, [name]: value },
            }));
        } else {
            setProfile((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError("");

        try {
            const response = await authClient.put(
                "/profile",
                {
                    first_name: profile.name.first_name,
                    last_name: profile.name.last_name,
                    middle_name: profile.name.middle_name,
                    org: profile.org,
                    role: profile.role,
                    email: profile.email
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setPopupMessage("Profile updated successfully!");
                setIsSuccess(true);
                setShowPopup(true);
                setIsEditing(false);
                setOriginalProfile(profile);
            } else {
                throw new Error("Failed to update profile.");
            }
        } catch (err) {
            console.error("Profile update error:", err);
            setPopupMessage("Could not update profile. Please try again.");
            setIsSuccess(false);
            setShowPopup(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setProfile(originalProfile);
        setIsEditing(false);
    };

    const closePopup = () => {
        setShowPopup(false);
        setPopupMessage("");
        setIsSuccess(false);
    };

    if (isLoading) return <div className="loading">Loading profile...</div>;

    return (
        <div>
            <div className="navbar">
                <a href="/dashboard">Home</a>
                <div className="account-container">
                    <a href="#" className="account-link">My Account</a>
                    <div className="account-dropdown">
                        <a href="/user/profile">Profile</a>
                        <a href="/user/sessions">Sessions</a>
                    </div>
                </div>
                <a href="/user/logout">Logout</a>
            </div>
            <div className="profile-wrapper">
                <div className="profile-card">
                    <div className="profile-header">
                        <div className="avatar">👤</div>
                        <h2>{profile.name.first_name} {profile.name.last_name}</h2>
                        <p>{profile.email}</p>
                    </div>

                    <div className="profile-body">
                        <div className="profile-field">
                            <label>First Name:</label>
                            <input
                                type="text"
                                name="first_name"
                                value={profile.name.first_name}
                                onChange={handleChange}
                                readOnly={!isEditing}
                            />
                        </div>

                        <div className="profile-field">
                            <label>Middle Name:</label>
                            <input
                                type="text"
                                name="middle_name"
                                value={profile.name.middle_name}
                                onChange={handleChange}
                                readOnly={!isEditing}
                            />
                        </div>

                        <div className="profile-field">
                            <label>Last Name:</label>
                            <input
                                type="text"
                                name="last_name"
                                value={profile.name.last_name}
                                onChange={handleChange}
                                readOnly={!isEditing}
                            />
                        </div>

                        <div className="profile-field">
                            <label>Role:</label>
                            <input
                                type="text"
                                name="role"
                                value={profile.role}
                                onChange={handleChange}
                                readOnly={!isEditing}
                            />
                        </div>

                        <div className="profile-field">
                            <label>Organization:</label>
                            <input
                                type="text"
                                name="org"
                                value={profile.org}
                                onChange={handleChange}
                                readOnly={!isEditing}
                            />
                        </div>
                    </div>

                    <div className="button-container">
                        {!isEditing ? (
                            <button className="edit-button" onClick={handleEdit}>Edit Profile</button>
                        ) : (
                            <>
                                <button className="save-button" onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? "Saving..." : "Save Changes"}
                                </button>
                                <button className="cancel-button" onClick={handleCancel}>Cancel</button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {showPopup && (
                <div className={styles.popupOverlay}>
                    <div className={styles.popup}>
                        <h3>{isSuccess ? "Success" : "Error"}</h3>
                        <p>{popupMessage}</p>
                        <button onClick={closePopup} className="popup-close-button">
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
